import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { AppBar, Autocomplete, Box, Button, Chip, Divider, FormControlLabel, FormGroup, IconButton, MenuItem, Switch, TextField, Toolbar, Typography, useMediaQuery, type Theme } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import * as yup from 'yup';
import { type MaxLocations } from '../../../../shared/types';
import { drawerStateAtom } from '../../../atoms';
import { useCreateAdminMaxLocation, useDeleteAdminMaxLocation, useUpdateAdminMaxLocation } from '../../../hooks/useAdminMaxLocations';
import { useDeleteDialog } from '../../../hooks/useDeleteDialog';
import { useSimpleAlert } from '../../../hooks/useSimpleAlert';
import { closedMixinToolbar, openedMixinToolbar } from '../../helpers';
import { useNavigate } from 'react-router';

type Props = {
    maxLocation?: MaxLocations | null;
};

const initialValues = {
    name: '',
    description: '',
    latitude: 0,
    longitude: 0,
    targetHours: [] as number[],
    active: true,
    units: 'imperial',
} as MaxLocations;

const validationSchema = yup.object().shape({
    name: yup.string().required('Required'),
    description: yup.string().required('Required'),
    latitude: yup.number().required('Required'),
    longitude: yup.number().required('Required'),
    targetHours: yup.array().of(yup.number().required()).min(1, 'At least one target hour is required').required(),
    active: yup.boolean().required(),
    units: yup.string().oneOf(['metric', 'imperial']).required('Required'),
});

const TARGET_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

export const MaxLocationForm = ({ maxLocation = null }: Props) => {
    const navigate = useNavigate();
    const [drawerState] = useAtom(drawerStateAtom);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { mutate: updateAdminMaxLocation } = useUpdateAdminMaxLocation();
    const { mutate: createAdminMaxLocation } = useCreateAdminMaxLocation();
    const { mutate: deleteAdminMaxLocation } = useDeleteAdminMaxLocation();
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { showSimpleAlert } = useSimpleAlert();

    const onBack = () => {
        navigate('/admin/max-locations');
    };

    const onSubmit = async (values: MaxLocations, { setErrors }: FormikHelpers<MaxLocations>) => {
        const { _id } = values;
        const strippedValues = validationSchema.cast(values, { stripUnknown: true });

        if (_id) {
            updateAdminMaxLocation(
                { id: _id.toString(), data: strippedValues },
                {
                    onSuccess: (data) => {
                        showSimpleAlert({ title: 'Location successfully updated' });
                        formik.resetForm({ values: data });
                    },
                },
            );
        } else {
            createAdminMaxLocation(strippedValues, {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Location successfully created' });
                    formik.resetForm({ values: initialValues });
                },
            });
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleShowDeleteDialog = () => {
        showDeleteDialog({ title: `Confirm DELETE of location ${maxLocation?.name}`, onDelete: handleDeleteLocation });
    };

    const handleDeleteLocation = () => {
        if (maxLocation) {
            deleteAdminMaxLocation(maxLocation._id.toString(), {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Location successfully deleted', severity: 'info' });
                    onBack();
                },
                onError: () => {
                    showSimpleAlert({ title: 'Unable to delete the location', severity: 'error' });
                },
            });
        }
    };

    useEffect(() => {
        const values = maxLocation ? { ...initialValues, ...maxLocation } : initialValues;

        formik.resetForm({ values });
    }, [maxLocation]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.sm, p: 3, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={onBack} startIcon={<NavigateBeforeIcon />}>
                            Max Locations
                        </Button>
                        {maxLocation && (
                            <Typography variant="h6" gutterBottom={false}>
                                {maxLocation.name}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Location Details
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.sm,
                                p: 3,
                                mb: 8,
                            }}>
                            <TextField
                                aria-describedby="name-text"
                                label="Name"
                                placeholder="Name"
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                            <TextField
                                aria-describedby="description-text"
                                label="Description"
                                placeholder="Description"
                                type="text"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                            <TextField
                                aria-describedby="latitude-text"
                                label="Latitude"
                                placeholder="Latitude"
                                type="number"
                                name="latitude"
                                value={formik.values.latitude}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.latitude)}
                                helperText={formik.touched.latitude && formik.errors.latitude}
                            />
                            <TextField
                                aria-describedby="longitude-text"
                                label="Longitude"
                                placeholder="Longitude"
                                type="number"
                                name="longitude"
                                value={formik.values.longitude}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.longitude)}
                                helperText={formik.touched.longitude && formik.errors.longitude}
                            />
                            <Autocomplete
                                multiple
                                options={TARGET_HOUR_OPTIONS}
                                value={formik.values.targetHours ?? []}
                                getOptionLabel={(option) => `${option}:00`}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={`${option}:00`} {...getTagProps({ index })} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Target Hours"
                                        error={Boolean(formik.touched.targetHours && formik.errors.targetHours)}
                                        helperText={formik.touched.targetHours && formik.errors.targetHours ? String(formik.errors.targetHours) : ''}
                                    />
                                )}
                                onChange={(_, value) => formik.setFieldValue('targetHours', value)}
                            />
                            <TextField
                                select
                                label="Units"
                                name="units"
                                value={formik.values.units || 'imperial'}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.units)}
                                helperText={formik.touched.units && formik.errors.units}>
                                <MenuItem value="imperial">Imperial</MenuItem>
                                <MenuItem value="metric">Metric</MenuItem>
                            </TextField>
                            <FormGroup aria-label="position" row>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            name="active"
                                            onChange={(_, checked) => formik.setFieldValue('active', checked)}
                                            checked={Boolean(formik.values.active)}
                                            value={formik.values.active}
                                        />
                                    }
                                    label="Active"
                                    labelPlacement="end"
                                />
                            </FormGroup>
                        </Box>
                    </form>
                </Box>
            </Box>
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.sm,
                    })}>
                    {maxLocation && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
